import React, { useEffect, useState } from 'react';
import {
  createProduct,
  fetchProducts,
  type InsuranceProduct,
} from '../api/insurance';

export default function Products() {
  const [products, setProducts] = useState<InsuranceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: string; msg: string } | null>(null);

  const [form, setForm] = useState({
    name: '',
    premium: '',
    coverage: '',
    maxPayout: '',
  });

  const load = async () => {
    try {
      const data = await fetchProducts();
      setProducts(data);
    } catch {
      /* keep current */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.premium || !form.coverage || !form.maxPayout) {
      setAlert({ type: 'warning', msg: 'All fields are required.' });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      await createProduct({
        name: form.name,
        premium: Number(form.premium),
        coverage: Number(form.coverage),
        maxPayout: Number(form.maxPayout),
        status: 'active',
      });
      setAlert({ type: 'success', msg: 'Product created successfully.' });
      setForm({ name: '', premium: '', coverage: '', maxPayout: '' });
      await load();
    } catch (e) {
      setAlert({ type: 'warning', msg: String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s: string) => {
    const cls = s === 'active' ? 'safe' : s === 'expired' ? 'warning' : 'active';
    return <span className={`insurance-badge ${cls}`}>{s}</span>;
  };

  return (
    <div>
      <header className="insurance-header">
        <h1>Insurance Products</h1>
        <p>Create and manage task failure insurance products</p>
      </header>

      {alert && (
        <div className={`insurance-alert ${alert.type}`}>{alert.msg}</div>
      )}

      {/* ── Create Product ── */}
      <div className="insurance-card" style={{ marginBottom: 24 }}>
        <div className="insurance-card-header">
          <span className="insurance-card-title">
            <span className="insurance-icon">📦</span>
            Create Product
          </span>
        </div>
        <form onSubmit={handleSubmit} className="insurance-form">
          <div className="insurance-form-row">
            <div className="insurance-form-group">
              <label className="insurance-label">Product Name</label>
              <input
                className="insurance-input"
                placeholder="Agent Failure Basic"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="insurance-form-group">
              <label className="insurance-label">Premium (AGU)</label>
              <input
                className="insurance-input"
                type="number"
                placeholder="100"
                value={form.premium}
                onChange={(e) => setForm({ ...form, premium: e.target.value })}
              />
            </div>
          </div>
          <div className="insurance-form-row">
            <div className="insurance-form-group">
              <label className="insurance-label">Coverage (%)</label>
              <input
                className="insurance-input"
                type="number"
                placeholder="80"
                value={form.coverage}
                onChange={(e) => setForm({ ...form, coverage: e.target.value })}
              />
            </div>
            <div className="insurance-form-group">
              <label className="insurance-label">Max Payout (AGU)</label>
              <input
                className="insurance-input"
                type="number"
                placeholder="5000"
                value={form.maxPayout}
                onChange={(e) => setForm({ ...form, maxPayout: e.target.value })}
              />
            </div>
          </div>
          <button className="insurance-button" type="submit" disabled={submitting}>
            {submitting ? 'Creating…' : '📦 Create Product'}
          </button>
        </form>
      </div>

      {/* ── Product List ── */}
      <div className="insurance-card">
        <div className="insurance-card-header">
          <span className="insurance-card-title">
            <span className="insurance-icon">📋</span>
            All Products
          </span>
          <span className="insurance-badge active">{products.length}</span>
        </div>
        {loading ? (
          <div className="insurance-loading">Loading…</div>
        ) : products.length === 0 ? (
          <div className="insurance-empty">No products yet. Create one above.</div>
        ) : (
          <table className="insurance-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Premium</th>
                <th>Coverage</th>
                <th>Max Payout</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={i}>
                  <td>{p.name}</td>
                  <td>{p.premium} AGU</td>
                  <td>{p.coverage}%</td>
                  <td>{p.maxPayout} AGU</td>
                  <td>{statusBadge(p.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Coverage Tiers ── */}
      <div className="insurance-card" style={{ marginTop: 24 }}>
        <div className="insurance-card-header">
          <span className="insurance-card-title">
            <span className="insurance-icon">🏷️</span>
            Coverage Tiers
          </span>
        </div>
        <div className="insurance-tiers">
          {[
            { tier: 'Basic', pct: 50, premium: 50, color: 'var(--ins-muted, #94a3b8)' },
            { tier: 'Standard', pct: 75, premium: 100, color: 'var(--ins-blue, #3b82f6)' },
            { tier: 'Premium', pct: 90, premium: 200, color: 'var(--ins-green, #10b981)' },
            { tier: 'Enterprise', pct: 100, premium: 500, color: 'var(--ins-gold, #f59e0b)' },
          ].map((t) => (
            <div className="insurance-tier-item" key={t.tier}>
              <div className="insurance-tier-header">
                <span>{t.tier}</span>
                <span style={{ color: t.color, fontWeight: 700 }}>{t.pct}% Coverage</span>
              </div>
              <div className="insurance-coverage">
                <div className="insurance-coverage-bar">
                  <div className="insurance-coverage-fill" style={{ width: `${t.pct}%` }} />
                </div>
              </div>
              <div className="insurance-stat-label">{t.premium} AGU / task</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
